import React, { useCallback, useEffect, useState } from 'react';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Box, IconButton } from '@mui/material';
import { tss } from 'tss-react/mui';
interface pictureCarouselProps {
  displayDots?: boolean;
  arrowsPosition: 'Middle' | 'Bottom';
  autoTransition?: boolean;
  shouldCoverBg?: boolean;
}
export const PictureCarousel: React.FC<
  pictureCarouselProps & React.PropsWithChildren<object>
> = ({
  children,
  displayDots,
  arrowsPosition,
  autoTransition,
  shouldCoverBg
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const childrenCount = React.Children.count(children);

  const onNextClick = useCallback(() => {
    if (activeIndex < childrenCount - 1) {
      setActiveIndex(activeIndex + 1);
    } else {
      setActiveIndex(0);
    }
  }, [activeIndex, childrenCount]);

  const onPrevClick = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    } else {
      setActiveIndex(childrenCount - 1);
    }
  };

  useEffect(() => {
    if (autoTransition) {
      const intervalId = setInterval(() => {
        onNextClick();
      }, 4000);

      return () => clearInterval(intervalId);
    }
  }, [activeIndex, autoTransition, onNextClick]);

  const { classes } = useStyles({
    arrowsPosition,
    shouldCoverBg: !!shouldCoverBg
  });
  return (
    <>
      <div className={classes.container}>
        {React.Children.map(children, (child, index) => (
          <Box
            className={classes.slide}
            key={index}
            sx={{
              opacity: index === activeIndex ? 1 : 0,
              zIndex: index === activeIndex ? 1 : 0
            }}
          >
            {child}
          </Box>
        ))}
      </div>
      {React.Children.count(children) > 1 && (
        <div className={classes.controls}>
          <IconButton color="primary" size="small" onClick={onPrevClick}>
            <ArrowBack />
          </IconButton>
          {displayDots && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {React.Children.map(children, (_child, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: ({ palette }) =>
                      index === activeIndex ? palette.primary.main : 'grey.500',
                    cursor: 'pointer'
                  }}
                  onClick={() => setActiveIndex(index)}
                />
              ))}
            </Box>
          )}
          <IconButton color="primary" size="small" onClick={onNextClick}>
            <ArrowForward />
          </IconButton>
        </div>
      )}
    </>
  );
};

const useStyles = tss
  .withParams<{ arrowsPosition: 'Middle' | 'Bottom'; shouldCoverBg: boolean }>()
  .create(({ theme, arrowsPosition, shouldCoverBg }) => ({
    container: {
      position: 'relative',
      display: 'flex',
      margin: 0,
      padding: 0,
      width: '100%',
      height: shouldCoverBg ? '100%' : undefined,
      aspectRatio: arrowsPosition === 'Middle' ? 'inherit' : 1 / 1
    },
    slide: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: '100%',
      width: '100%',
      transition: 'opacity 2s',
      img: {
        width: '100%',
        objectFit: 'contain',
        ...(shouldCoverBg
          ? {
              height: '100%',
              objectFit: 'cover'
            }
          : {})
      }
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: theme.spacing(0.5),
      position: 'relative',
      zIndex: 2,
      padding: arrowsPosition === 'Middle' ? '0 30px' : 0,
      top: arrowsPosition === 'Middle' ? '-50%' : 0,
      svg: {
        fill: arrowsPosition === 'Middle' ? 'white' : 'inherit'
      },
      [theme.breakpoints.down('md')]: {
        padding: arrowsPosition === 'Middle' ? '0 10px' : 0
      }
    }
  }));
